import ApiService from '@/services/apiService';
import { mapState, mapGetters } from 'vuex';
import moment from 'moment';
import get from 'lodash/get';
import debounce from 'lodash/debounce';
import { codemirror } from 'vue-codemirror';
import 'codemirror/lib/codemirror.css';
import 'codemirror/theme/base16-dark.css';
import 'codemirror/theme/duotone-light.css';
import 'codemirror/mode/javascript/javascript.js';
import { userItemsPerListMixin } from '@/utils/vuex';
import chartMixin from '@/components/niotaClassic/charts/chartMixin';
import stateHistoryFetchMixin from '@/mixins/stateHistoryFetch';
const defaultItemsPerPage = 10;
const defaultTableOptions = {
  sortBy: ['time'],
  sortDesc: [true],
  page: 1,
  itemsPerPage: defaultItemsPerPage
};
export default {
  name: 'CoreDtwinStateHistory',
  components: {
    codemirror
  },
  mixins: [userItemsPerListMixin, chartMixin, stateHistoryFetchMixin],
  props: {
    state: {
      type: Object,
      required: true
    },
    minYAxis: {
      type: Number,
      required: false,
      default: null
    },
    maxYAxis: {
      type: Number,
      required: false,
      default: null
    }
  },
  data() {
    return {
      packetHistory: [],
      packetAggHistory: [],
      isLoadingTable: false,
      isLoadingChart: false,
      hasChartError: false,
      hasTableError: false,
      chartLinesCurved: true,
      chartOmitNull: true,
      queryStartDate: new Date(new Date() - 86400000),
      queryEndDate: new Date(),
      customDatePicked: false,
      serverItemsLength: -1,
      tableOptions: {
        ...defaultTableOptions
      },
      priorTableOptions: {},
      expandedEvent: [],
      tableFooterProps: {
        itemsPerPageText: this.$i18n_t('Common.rowsPerPage'),
        itemsPerPageOptions: [10, defaultTableOptions.itemsPerPage, 20, 50]
      },
      cmOptions: {
        tabSize: 4,
        mode: 'application/ld+json',
        theme: this.$store.state.app.UiTheme ? 'base16-dark' : 'duotone-light',
        lineNumbers: false,
        line: true,
        foldGutter: true,
        styleActiveLine: true,
        styleSelectedText: true,
        readOnly: true,
        hintOptions: {
          completeSingle: false
        },
        lineWrapping: true,
        viewportMargin: Infinity
      },
      tabs: null,
      interval: '1h',
      // chart fetch options
      min: false,
      max: false,
      average: true,
      latest: false,
      // do not aggregate anything, but output the latest value of a time-interval
      rawData: false,
      // return the latest 50 raw values (unaggregated)

      chartData: {
        data: []
      }
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color']),
    ...mapGetters({
      sessionUserId: 'authentication/userId'
    }),
    stateIsNumber() {
      return this.state.properties.valueType === 'number';
    },
    chartConfig() {
      return {
        ...(this.chartLinesCurved ? {} : {
          elements: {
            line: {
              tension: 0
            }
          }
        }),
        ...(this.chartOmitNull ? {} : {
          spanGaps: true
        })
      };
    },
    isEmptyChart() {
      return !this.isLoadingChart && !this.hasChartError && (this.chartData.data.avg || []).length === 0 && (this.chartData.data.min || []).length === 0 && (this.chartData.data.max || []).length === 0 && (this.chartData.data.latest || []).length === 0 && (this.chartData.data.rawData || []).length === 0;
    },
    items() {
      return [{
        time: '5min',
        timeVal: '5m',
        disabled: false
      }, {
        time: '15min',
        timeVal: '15m',
        disabled: false
      }, {
        time: '1h',
        timeVal: '1h',
        disabled: false
      }, {
        time: `1 ${this.$i18n_t('Common.day')}`,
        timeVal: '1d',
        disabled: false
      }, {
        time: `1 ${this.$i18n_t('Common.week')}`,
        timeVal: '1w',
        disabled: false
      }];
    },
    tableHeaders() {
      return [{
        sortable: true,
        text: this.$i18n_t('Core.DateTimePicker.dateTime'),
        value: 'time'
      }, {
        sortable: true,
        text: `${get(this.state, 'properties.title', this.state.key)} ${get(this.state, 'properties.unit', '')}`,
        value: 'value'
      }, {
        text: '',
        value: 'data-table-expand'
      }];
    }
  },
  watch: {
    tableOptions: {
      handler() {
        this.loadTableData();
      },
      deep: true
    },
    queryItemsInflux: debounce(function () {
      this.loadTableData();
      this.loadChartData();
    }, 500, {
      leading: true
    }),
    state: {
      handler() {
        this.loadTableData();
        this.loadChartData();
      },
      deep: true
    },
    interval: {
      handler() {
        this.loadChartData();
      }
    },
    average: {
      handler() {
        this.loadChartData();
      }
    },
    min: {
      handler() {
        this.loadChartData();
      }
    },
    max: {
      handler() {
        this.loadChartData();
      }
    },
    latest: {
      handler() {
        this.loadChartData();
      }
    },
    rawData: {
      handler() {
        if (this.rawData) {
          this.min = false;
          this.max = false;
          this.average = false;
          this.latest = false;
        }
        // if disabled again, enable at least 1 option by default again
        if (!this.rawData) {
          const options = ['min', 'max', 'average', 'latest'];
          // check if OTHER options are selected as well
          if (options.map(o => this[o]).filter(o => o).length === 0) {
            this.average = true;
          }
        }
        this.loadChartData();
      }
    }
  },
  created() {
    const page = parseInt(this.$route.query.page, 10) || defaultTableOptions.page;
    const search = this.$route.query.search || '';
    if (search) {
      this.isInitialSearch = true;
      this.searchItem = search;
    }
    this.tableOptions.page = page;
  },
  mounted() {
    this.disableTimeIntervals(this.queryEndDate, this.queryStartDate);
    this.loadTableData();
    this.loadChartData();
  },
  methods: {
    shouldOptionCheckboxBeDisabled(option) {
      if (option !== 'rawData' && this.rawData) {
        return true;
      }
      if (option === 'rawData') {
        // always allow the "rawData" checkbox, see handler that will disable all other options on selection
        return false;
      }
      const options = ['min', 'max', 'average', 'latest'].filter(o => o !== option);
      // check if OTHER options are selected as well
      return options.map(o => this[o]).filter(o => o).length === 0;
    },
    reloadData() {
      if (!this.customDatePicked) {
        this.queryEndDate = new Date();
      }
      // NOTE: this is triggered externally if the history was updated.
      this.loadTableData();
      this.loadChartData();
    },
    loadChartData() {
      if (!this.stateIsNumber) {
        this.isLoadingChart = false;
        return [];
      }
      if (this.isLoadingChart) {
        return;
      }
      this.isLoadingChart = true;
      this.hasChartError = false;
      let extraParams = {
        interval: this.interval,
        min: this.min,
        max: this.max,
        latest: this.latest,
        rawData: this.rawData
      };
      ApiService.influxStatesSearchAggregated(this, [this.state.dtwinId], [this.state.id], {}, this.queryStartDate, this.queryEndDate, 'avg', extraParams).then(chartResponse => {
        // normalize API response to consistent format { avg: [], min: [], max: [] }
        const normalized = chartResponse;
        let hasDataWithTooManyItems = false;

        // convert all data values to fixed decimal places
        const chartData = Object.keys(normalized).reduce((acc, key) => {
          let normedData = normalized[key].map(item => ({
            time: item.time,
            value: typeof item.value === 'number' ? parseFloat(item.value.toFixed(4)) : this.state.properties.valueType === 'boolean' && item.value === null ? 0 : item.value
          }));
          // sort data descendingly
          // if there are too many datapoints, this will show the newest one first and cut off old data
          // since the chart-lib seems to only be able to display ~1000 entries
          // https://digimondo.atlassian.net/browse/RD-6253?focusedCommentId=40677
          normedData = normedData.reverse();
          if (normedData.length > 1000) {
            hasDataWithTooManyItems = true;
          }
          return {
            ...acc,
            [key]: normedData
          };
        }, {});

        // set main label for chart based on stat title and unit
        this.chartData.label = `${this.state.properties.title}`;
        if (this.state.properties.unit) {
          this.chartData.label += ` (${this.state.properties.unit})`;
        }

        // set chart data
        this.chartData.data = {
          ...(this.average ? {
            avg: chartData.avg.slice(0, 1000)
          } : {}),
          ...(this.max ? {
            max: (chartData.max || []).slice(0, 1000)
          } : {}),
          ...(this.min ? {
            min: (chartData.min || []).slice(0, 1000)
          } : {}),
          ...(this.latest ? {
            latest: (chartData.latest || []).slice(0, 1000)
          } : {}),
          ...(this.rawData ? {
            rawData: (chartData.rawData || []).slice(0, 1000)
          } : {})
        };
        this.chartData.hasDataWithTooManyItems = hasDataWithTooManyItems;
      }).catch(error => {
        console.error(error);
        this.hasChartError = true;
      }).finally(() => {
        this.isLoadingChart = false;
      });
    },
    loadTableData() {
      if (this.isLoadingTable) {
        return;
      }
      this.isLoadingTable = true;
      this.hasTableError = false;
      const {
        sortBy,
        sortDesc,
        page,
        itemsPerPage
      } = this.tableOptions;
      const sortItem = sortBy && sortBy.length ? sortBy[0] : 'time';
      const doSortDesc = sortDesc && sortDesc.length ? sortDesc[0] : true;
      const sortDirection = doSortDesc ? 'DESC' : 'ASC';
      const usedPage = page ? page - 1 : 0;
      const usedItemsPerPage = itemsPerPage || defaultItemsPerPage;
      this.loadDetailledHistoryData(this.state.id, this.state.dtwinId, sortItem, sortDirection, usedPage, usedItemsPerPage).then(res => {
        this.serverItemsLength = res.totalCount;
        this.packetHistory = res.items.map((item, index) => ({
          id: `${usedPage}-${index + 1}`,
          ...item
        }));
      }).catch(error => {
        console.error(error);
        this.hasTableError = true;
      }).finally(() => {
        this.isLoadingTable = false;
      });
    },
    outputValue(value) {
      if (value === null) {
        return 'null';
      }
      try {
        const jsonObject = typeof value === 'object' ? value : JSON.parse(value);
        // parse and then stringify again to get a nice format
        return JSON.stringify(jsonObject, null, 1);
      } catch (error) {
        // on error we return outside of try/catch
      }
      return value;
    },
    localizeDate(a) {
      if (a) {
        moment.locale(this.$store.state.app.language || window.navigator.userLanguage || window.navigator.language);
        return moment(a).format('L LTS');
      }
      return '-';
    },
    handleDatePicked(dateObject) {
      this.queryStartDate = new Date(dateObject.from * 1000);
      this.queryEndDate = new Date(dateObject.to * 1000);
      // NOTE: for the "reloadData" event, make sure that the end-date is always up-to-date
      // so refresh it automatically if it is "close enough"
      this.customDatePicked = Math.abs(this.queryEndDate - new Date()) > 60000;
      this.disableTimeIntervals(this.queryEndDate, this.queryStartDate);
      //Set current table page to the first one
      this.tableOptions.page = 1;
    },
    disableTimeIntervals(endDate, startDate) {
      const diffInMils = new Date(endDate) - new Date(startDate);
      const diffInMins = Math.floor(diffInMils / 60000);
      const diffInDays = diffInMils / (1000 * 60 * 60 * 24);
      this.items.forEach(item => {
        item.disabled = false;
      });
      if (diffInDays > 30) {
        // disable first three options
        this.items[0].disabled = true;
        this.items[1].disabled = true;
        this.items[2].disabled = true;
        this.interval = '1d';
      } else if (diffInDays < 30) {
        const durationsInMins = [5, 15, 60, 1440, 10080, 43200];
        let upperBoundDurationIndex = durationsInMins.findIndex(x => diffInMins < x);
        // if smaller or equal to 5 then [5], otherwise the upperBoundDurationIndex.
        // example1: upperBoundDuration is 60 -> index: 2, then allowed is index 2
        let maximumAllowedDurationIndex = upperBoundDurationIndex ? upperBoundDurationIndex : 1;
        // follow example 1: allowedDurations = ['5m', '15m']
        this.items.forEach((item, itemIndex) => {
          if (itemIndex >= maximumAllowedDurationIndex) {
            item.disabled = true;
          }
        });

        // use the minimum between max allowed and stored interval
        // if max allowed is 1h and stored interval is 15m, then use 15m
        // if max allowed is 1h and stored interval is 1d, then use 1h
        const currentIntervalIndex = this.items.findIndex(x => x.timeVal === this.interval);
        this.interval = this.items[Math.min(currentIntervalIndex, maximumAllowedDurationIndex - 1)].timeVal;
      }
    },
    setCurrentTablePage(value) {
      this.tableOptions.page = value;
    }
  }
};